import React from "react";
import { Box, getColor, Icons, Toggle, Tooltip, Typography } from "fds";
import "./index.scss";
import { LoginDto, permissionDTO } from "../../types/auth";
import utilsCommon from "../../shared/utils/common";
import SetPolicyPage from "../SePolicyPage";
import dayjs from "dayjs";
import { getUserInfoFromLocalStorage } from "../../shared/utils/auth";
import { t } from "i18next";

interface WorkgroupAuthSettingTabProps {
  policy?: permissionDTO;
  isWorkgroupManager?: boolean;
  changeWorkgroupPolicy: (newPolicy: permissionDTO) => void;
}
const WorkgroupAuthSettingTab: React.FC<WorkgroupAuthSettingTabProps> = ({
  policy,
  isWorkgroupManager,
  changeWorkgroupPolicy,
}) => {
  const user = getUserInfoFromLocalStorage() as string | "";
  const loginDto: LoginDto = JSON.parse(user);
  const screenWatermark = utilsCommon.convertWatermark(
    loginDto?.userInfo.userName,
    loginDto?.userInfo.userId,
    "test.xlsx",
    utilsCommon.renderShortDateFormat(dayjs().valueOf()),
    utilsCommon.renderDateFormat(dayjs().valueOf()),
    loginDto?.ecoUserInfo.remoteAddr,
    loginDto?.ecoCommonConfig.watermarkPattern
  );
  /**함수 정리 */
  /** UI: 허용일 경우 컴포넌트*/
  const renderAllowBox = (label?: string) => {
    return (
      <Box alignItems={"center"}>
        <Icons variant="fill" label="dot" stroke={"#3182F6"} />
        <Typography type="body2_rg" classes="mg ml-6">
          {label ?? t("common.allow")}
        </Typography>
      </Box>
    );
  };
  /** UI: 비허용일 경우 컴포넌트*/
  const renderRejectBox = (label?: string) => {
    return (
      <Box alignItems={"center"}>
        <Icons variant="fill" label="dot" stroke={"#7287A2"} />
        <Typography type="body2_rg" classes="mg ml-6">
          {label ?? t("common.notAllow")}
        </Typography>
      </Box>
    );
  };
  /** UI: 우측에 나오는 컴포넌트*/
  const renderPolicyToStr = (type: string) => {
    if (!policy?.policy) return;
    if (type === "revision") {
      return policy?.policy?.revision ? renderAllowBox() : renderRejectBox();
    } else if (type === "download") {
      return policy?.policy?.download ? renderAllowBox() : renderRejectBox();
    } else if (type === "allowViewCount") {
      return !policy?.policy?.allowViewCount ||
        policy?.policy?.allowViewCount === 0
        ? t("file.policy.infinite")
        : policy?.policy?.allowViewCount + t("file.policy.count");
    } else if (type === "allowDays") {
      return policy?.policy?.allowDays === undefined ||
        policy?.policy?.allowDays <= -1
        ? t("file.policy.noDeadline")
        : policy?.policy?.allowDays +
            `${t("common.days")} ${t("file.dependUpload")}`;
    } else if (type === "webView") {
      return utilsCommon.checkSupportPolicy("Web") === "SUPPORT_ACCEPTED" ? (
        !policy.policy.webView ? (
          renderRejectBox()
        ) : (
          <Box direction="column">
            {renderAllowBox()}
            <Typography classes="mg mt-8" type="body2_rg">
              {t("file.policy.screenWatermark")}
            </Typography>
            <Tooltip message={screenWatermark}>
              <Typography
                type="body4_rg"
                exactColor={getColor("COLOR_TEXT_SECONDARY")}
                classes="mg mt-4"
                ellipsis
              >
                {screenWatermark}
              </Typography>
            </Tooltip>
          </Box>
        )
      ) : utilsCommon.checkSupportPolicy("Web") === "ADMIN_NOT_ALLOW" ? (
        renderRejectBox(t("file.policy.notPolicy"))
      ) : (
        renderRejectBox(t("file.policy.notsupport"))
      );
    } else if (type === "mobile") {
      return utilsCommon.checkSupportPolicy("Mobile") === "SUPPORT_ACCEPTED"
        ? renderAllowBox()
        : utilsCommon.checkSupportPolicy("Mobile") === "ADMIN_NOT_ALLOW"
        ? renderRejectBox(t("file.policy.notPolicy"))
        : renderRejectBox(t("file.policy.notsupport"));
    } else if (type === "mac") {
      return utilsCommon.checkSupportPolicy("Mac") === "SUPPORT_ACCEPTED"
        ? renderAllowBox()
        : utilsCommon.checkSupportPolicy("Mac") === "ADMIN_NOT_ALLOW"
        ? renderRejectBox(t("file.policy.notPolicy"))
        : renderRejectBox(t("file.policy.notsupport"));
    } else if (type === "internalAllow") {
      return policy.policy.additionalViewPermission === "INTERNAL_USER"
        ? renderAllowBox()
        : renderRejectBox();
    } else if (type === "security") {
      return !policy.policy.wrapping ? (
        renderRejectBox()
      ) : (
        <Box alignItems={"center"}>
          <Icons variant="fill" label="dot" stroke={"#3182F6"} />
          <Typography type="body2_rg" classes="mg ml-6">
            {policy.policy.drmPerm?.length === 0
              ? `${t("file.policy.normal")} (${t("common.allow")}: ${t(
                  "file.policy.print"
                )},${t("file.policy.printScreen")},${t("file.policy.copyDoc")})`
              : `${t("file.policy.advanced")} (${t("file.policy.noAllow")} ${[
                  "print",
                  "print_screen",
                  "extract",
                ]
                  .filter((item) => !policy.policy?.drmPerm?.includes(item))
                  .map((item1) => {
                    return item1 === "print"
                      ? t("file.policy.print")
                      : item1 === "print_screen"
                      ? t("file.policy.printScreen")
                      : item1 === "extract"
                      ? t("file.policy.copyDoc")
                      : "";
                  })
                  .join(", ")})`}
          </Typography>
        </Box>
      );
    }
  };
  const renderUserMode = () => {
    return (
      <Box direction="column">
        <Typography type="subtitle2">
          {t("workgroup.workgroupDefaultPermssion")}
        </Typography>
        <div className="workgroupModal-body--header" />
        <Box classes="mg mt-20 workgroupModal-body--items">
          <Box>
            <Typography
              type="subtitle2"
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
              width={140}
            >
              {t("file.policy.revision")}
            </Typography>
            <Box classes="mg ml-10" maxWidth={"calc(100% - 150px)"}>
              {renderPolicyToStr("revision")}
            </Box>
          </Box>
          <Box>
            <Typography
              type="subtitle2"
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
              width={140}
            >
              {t("file.policy.download")}
            </Typography>
            <Typography
              type="body2_rg"
              classes="mg ml-10"
              maxWidth={"calc(100% - 150px)"}
              exactColor={getColor("COLOR_TEXT_PRIMARY")}
            >
              {renderPolicyToStr("download")}
            </Typography>
          </Box>
          {policy?.policy?.download && (
            <Box>
              <Typography
                type="subtitle2"
                exactColor={getColor("COLOR_TEXT_SECONDARY")}
                width={140}
              >
                {t("file.policy.advanded")}
              </Typography>
              <Typography
                type="body2_rg"
                classes="mg ml-10"
                maxWidth={"calc(100% - 150px)"}
                exactColor={getColor("COLOR_TEXT_PRIMARY")}
              >
                {renderPolicyToStr("security")}
              </Typography>
            </Box>
          )}
          <Box>
            <Typography
              type="subtitle2"
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
              width={140}
            >
              {t("file.policy.allowCount")}
            </Typography>
            <Typography
              type="body2_rg"
              classes="mg ml-10"
              maxWidth={"calc(100% - 150px)"}
              exactColor={getColor("COLOR_TEXT_PRIMARY")}
            >
              {renderPolicyToStr("allowViewCount")}
            </Typography>
          </Box>
          <Box>
            <Typography
              type="subtitle2"
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
              width={140}
            >
              {t("file.policy.allowPeriod")}
            </Typography>
            <Typography
              type="body2_rg"
              classes="mg ml-10"
              maxWidth={"calc(100% - 150px)"}
              exactColor={getColor("COLOR_TEXT_PRIMARY")}
            >
              {renderPolicyToStr("allowDays")}
            </Typography>
          </Box>
          {utilsCommon.checkVisibleOptions("webview") && (
            <Box>
              <Typography
                type="subtitle2"
                exactColor={getColor("COLOR_TEXT_SECONDARY")}
                width={140}
              >
                {t("file.policy.webSupport")}
              </Typography>
              <Typography
                type="body2_rg"
                classes="mg ml-10"
                maxWidth={"calc(100% - 150px)"}
                exactColor={getColor("COLOR_TEXT_PRIMARY")}
              >
                {renderPolicyToStr("webView")}
              </Typography>
            </Box>
          )}
          {utilsCommon.checkVisibleOptions("mobile") && (
            <Box>
              <Typography
                type="subtitle2"
                exactColor={getColor("COLOR_TEXT_SECONDARY")}
                width={140}
              >
                {t("file.policy.mobileSupport")}
              </Typography>
              <Typography
                type="body2_rg"
                classes="mg ml-10"
                maxWidth={"calc(100% - 150px)"}
                exactColor={getColor("COLOR_TEXT_PRIMARY")}
              >
                {renderPolicyToStr("mobile")}
              </Typography>
            </Box>
          )}
          {utilsCommon.checkVisibleOptions("mac") && (
            <Box>
              <Typography
                type="subtitle2"
                exactColor={getColor("COLOR_TEXT_SECONDARY")}
                width={140}
              >
                {t("file.policy.macSupport")}
              </Typography>
              <Typography
                type="body2_rg"
                classes="mg ml-10"
                maxWidth={"calc(100% - 150px)"}
                exactColor={getColor("COLOR_TEXT_PRIMARY")}
              >
                {renderPolicyToStr("mac")}
              </Typography>
            </Box>
          )}
          {window.eco.serviceType !== "OCI" &&
            utilsCommon.checkVisibleOptions("openSetting") && (
              <Box>
                <Typography
                  type="subtitle2"
                  exactColor={getColor("COLOR_TEXT_SECONDARY")}
                  width={140}
                >
                  {t("file.policy.internalAllow")}
                </Typography>
                <Typography
                  type="body2_rg"
                  classes="mg ml-10"
                  maxWidth={"calc(100% - 150px)"}
                  exactColor={getColor("COLOR_TEXT_PRIMARY")}
                >
                  {renderPolicyToStr("internalAllow")}
                </Typography>
              </Box>
            )}
        </Box>
      </Box>
    );
  };
  return isWorkgroupManager && policy ? (
    <SetPolicyPage
      policy={policy.policy}
      isWorkgroupManager={isWorkgroupManager}
      changePolicy={(newPolicy) =>
        changeWorkgroupPolicy({ ...policy, policy: newPolicy })
      }
    />
  ) : (
    renderUserMode()
  );
};
export default WorkgroupAuthSettingTab;
